import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, MapPin, Clock, Loader2, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { submitContact } from "../lib/api";
import { BRAND, telLink, waLink } from "../lib/data";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill name, phone and message");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent! We'll get back to you shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Could not send. Please try WhatsApp or call.");
    } finally {
      setLoading(false);
    }
  };

  const cards = [
    { icon: Phone, label: "Call Us", value: BRAND.phoneDisplay, href: telLink, color: "text-terracotta-600", bg: "bg-terracotta-100 border-terracotta-500/30" },
    { icon: MessageCircle, label: "WhatsApp", value: BRAND.phoneDisplay, href: waLink(), color: "text-approval", bg: "bg-approval/12 border-approval/25" },
    { icon: Mail, label: "Email", value: BRAND.email, href: `mailto:${BRAND.email}`, color: "text-forest-800", bg: "bg-forest-100 border-cream-300" },
  ];

  return (
    <div data-testid="contact-page" className="pt-28 pb-20 bg-cream-100 text-soil-700 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-block text-xs uppercase tracking-[0.2em] font-bold text-terracotta-600 mb-3">Get In Touch</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-forest-800">Let's Talk <span className="saffron-text">Approvals</span></h1>
          <p className="text-soil-500 mt-4">Speak with {BRAND.contactPerson} and the Loan Doctor team. Free consultation, zero obligation.</p>
          <p className="text-soil-500 mt-3 text-sm">
            Genesis Group is an All-in-One Business Center rooted in Bharat and connected to opportunity. Whether you want to
            explore a loan or funding solution, join Genesis Mall as a seller, become an agent or affiliate, enquire about a
            franchise or investment, or simply learn more about our ten business verticals, our team is here to help across
            120+ cities in India. Reach us by phone, WhatsApp or email — or send the message form below and we will respond
            promptly with clear, honest guidance and no obligation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
          {cards.map((c) => (
            <a key={c.label} href={c.href} target={c.label === "WhatsApp" ? "_blank" : undefined} rel={c.label === "WhatsApp" ? "noreferrer nofollow" : undefined}
              data-testid={`contact-card-${c.label.toLowerCase().replace(" ", "-")}`}
              className="card-cream rounded-2xl p-6 flex items-center gap-4 hover:border-forest-500/40 transition-all hover:-translate-y-1">
              <div className={`w-12 h-12 rounded-xl border flex items-center justify-center ${c.bg}`}>
                <c.icon className={`w-6 h-6 ${c.color}`} />
              </div>
              <div className="min-w-0">
                <div className="text-xs text-soil-500">{c.label}</div>
                <div className="font-semibold text-forest-800 break-all">{c.value}</div>
              </div>
            </a>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            className="card-cream rounded-2xl p-6 sm:p-8">
            <h2 className="font-heading text-2xl font-bold text-forest-800 mb-6">Send a Message</h2>
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="contact-name-input" placeholder="Full Name *" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
                <Input data-testid="contact-phone-input" placeholder="Phone *" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              </div>
              <Input data-testid="contact-email-input" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-white border-cream-300 h-11 text-forest-800" />
              <Textarea data-testid="contact-message-input" placeholder="How can we help you? *" value={form.message} onChange={(e) => set("message", e.target.value)} className="bg-white border-cream-300 text-forest-800 min-h-[120px]" />
              <Button type="submit" disabled={loading} data-testid="contact-submit-button"
                className="w-full forest-gradient text-cream-50 font-bold h-12 rounded-full">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send Message <Send className="w-4 h-4 ml-2" /></>}
              </Button>
            </form>
          </motion.div>

          <div className="space-y-6">
            <div className="card-cream rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <MapPin className="w-5 h-5 text-terracotta-600" />
                <h3 className="font-heading font-semibold text-forest-800">Our Reach</h3>
              </div>
              <p className="text-soil-500 text-sm">{BRAND.legal}. Serving clients across 120+ cities in India.</p>
              <div className="rounded-xl overflow-hidden mt-4 border border-cream-300 h-56">
                <iframe
                  title="location"
                  src="https://www.google.com/maps?q=India&output=embed"
                  className="w-full h-full grayscale contrast-125"
                  loading="lazy"
                />
              </div>
            </div>
            <div className="card-cream rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="w-5 h-5 text-terracotta-600" />
                <h3 className="font-heading font-semibold text-forest-800">Working Hours</h3>
              </div>
              <div className="space-y-1.5 text-sm text-soil-500">
                <div className="flex justify-between"><span>Mon – Sat</span><span className="text-forest-800">9:00 AM – 8:00 PM</span></div>
                <div className="flex justify-between"><span>Sunday</span><span className="text-forest-800">10:00 AM – 4:00 PM</span></div>
                <div className="flex justify-between"><span>WhatsApp Support</span><span className="text-approval">24×7</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
